<template>
  <v-form
    class="grid-container familyMember-details-grid"
    :id="'familyMember-details-grid-' + idPrefix"
    ref="form"
    v-model="valid"
    lazy-validation
  >
    <h2 style="grid-area: header">{{ title }}</h2>
    <v-row class="mt-10">
      <v-col class="custom-col">
        <span class="text-title">Biological relationship*</span>

        <v-select
          v-model="personType"
          :id="idPrefix + 'personType'"
          :rules="personType !== '' ? familyRoleRule : ruleNotNull"
          :items="familyRolesList"
          item-text="displayText"
          item-value="value"
          outlined
          hide-details="auto"
          class="fam-role-class"
        ></v-select>
      </v-col>

      <v-col class="custom-col">
        <span class="text-title">Affected by condition?</span>
        <v-text-field
          v-model="affected"
          :id="idPrefix + 'affected'"
          outlined
          :maxlength="limitRelationAffected"
          hide-details="auto"
        ></v-text-field>
        <span class="text-hint">e.g. mildly affected</span>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Sample type*</span>
        <v-select
          v-model="sampleType"
          :id="idPrefix + 'sampleType'"
          :items="processedSampleTypeList"
          item-text="displayText"
          item-value="value"
          outlined
          :rules="ruleNotNull"
          @change="handleSampleTypeChange"
          hide-details="auto"
        ></v-select>
      </v-col>
      <v-col
        v-if="
          sampleType === 'STORED_DNA' ||
            sampleType === 'STORED_DATA' ||
            sampleType === 'NON_VCGS_SAMPLE'
        "
      >
        <span class="text-title" v-if="sampleType === 'NON_VCGS_SAMPLE'"
          >Non-VCGS sample ID</span
        >
        <span class="text-title" v-else>VCGS sample ID</span>
        <v-text-field
          v-model="storedSampleId"
          :id="idPrefix + 'storedSampleId'"
          :rules="ruleStoredSampleId"
          outlined
          v-if="sampleType === 'STORED_DNA' || sampleType === 'STORED_DATA'"
          hide-details="auto"
        ></v-text-field>
        <v-text-field
          v-model="nonVCGSSampleID"
          :id="idPrefix + 'nonVCGSSampleID'"
          :rules="ruleStoredSampleId"
          outlined
          v-if="sampleType === 'NON_VCGS_SAMPLE'"
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col class="custom-col">
        <v-alert
          text
          :id="idPrefix + 'sampleTypeMsg'"
          type="warning"
          outlined
          icon="mdi-cash-usd-outline"
          v-if="sampleType === 'STORED_DATA'"
        >
          <b>Important:</b> This option is only available where a previous
          <span
            v-if="
              testType === 'mito_diagnostic' ||
                testType === 'whole_genome_sequencing'
            "
            >whole genome sequencing</span
          >
          <span v-else-if="testType === 'clinical_exome'">clinical exome</span>
          <span v-else>clinical exome or whole genome sequencing</span>
          test has been performed at VCGS. Stored data can be used for
          reanalysis. Upon submission, VCGS will review this request and contact
          you, if necessary, regarding feasibility and pricing.
        </v-alert>
        <v-alert
          text
          id="patient.sampleTypeMsg"
          type="warning"
          color="red"
          outlined
          icon="mdi-cash-usd-outline"
          class="mr-1"
          v-if="sampleType === 'NON_VCGS_SAMPLE'"
        >
          <b :icon="false">Important:</b> Fresh samples are required for
          testing. Extracted DNA can be accepted when a sample is precious/hard
          to obtain such as for prenatals, POCs, or from a deceased person. For
          any enquiries, please contact
          <a href="mailto:VCGSgenomics@vcgs.org.au">VCGSgenomics@vcgs.org.au</a
          >.
        </v-alert>
        <v-alert
          :id="idPrefix + 'sampleTypeMsg'"
          text
          type="warning"
          icon="mdi-cash-usd-outline"
          outlined
          v-if="
            sampleType === 'BLOOD' ||
              sampleType === 'SALIVA_NO_KIT' ||
              sampleType === 'NON_VCGS_SAMPLE'
          "
        >
          <b>Important:</b> Upon submission of this order, a pre-filled
          pathology collection form will be generated. Please download prior to
          leaving the order summary page or closing the browser, as the form
          will be inaccessible after this time.
        </v-alert>
        <v-alert
          text
          id="patient.sampleTypeMsg"
          type="warning"
          outlined
          v-if="sampleType === 'STORED_DNA'"
          icon="mdi-cash-usd-outline"
        >
          <b :icon="false">Important:</b>
          If sample is over 3 years old, please collect a fresh sample. You can
          check the age of the stored DNA from the first two digits of the
          sample ID (e.g. VCGS sample # 21WXXXXXX is from 2021). Exceptions can
          apply if a new collection is not possible/hard to obtain, please
          contact
          <a href="mailto:VCGSgenomics@vcgs.org.au">VCGSgenomics@vcgs.org.au</a>
          to discuss.
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="custom-col">
        <span class="text-title">Patient ID (if applicable)</span>
        <v-text-field
          v-model="externalId"
          :id="idPrefix + 'externalId'"
          outlined
          :maxlength="limitExternalID"
          hide-details="auto"
        ></v-text-field>
        <span class="text-hint"
          >Unique patient identification number. E.g. UR or MRN</span
        >
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Given name*</span>
        <v-text-field
          v-model="firstName"
          :id="idPrefix + 'firstName'"
          :rules="ruleNotNull"
          outlined
          :maxlength="limitGivenName"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Surname*</span>
        <v-text-field
          v-model="lastName"
          :id="idPrefix + 'lastName'"
          :rules="ruleNotNull"
          outlined
          :maxlength="limitSurname"
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row class="mb-4">
      <v-col class="custom-col">
        <span class="text-title">Date of birth*</span>
        <v-text-field
          v-model="dobdd"
          :id="idPrefix + 'dobdd'"
          :rules="dobddRule"
          outlined
          :maxlength="limitDay"
          label="Day"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="pt-9">
        <v-text-field
          v-model="dobmm"
          :id="idPrefix + 'dobmm'"
          :rules="dobmmRule"
          outlined
          label="Month"
          :maxlength="limitMonth"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="pt-9">
        <v-text-field
          v-model="dobyy"
          :id="idPrefix + 'dobyy'"
          :rules="dobyyRule"
          outlined
          label="Year"
          :maxlength="limitYear"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Ethnicity</span>
        <v-select
          v-model="ethnicity"
          :id="idPrefix + 'ethnicity'"
          :items="ethnicityList"
          item-text="displayText"
          item-value="value"
          outlined
          :maxlength="limitEthnicity"
          hide-details="auto"
        ></v-select>
      </v-col>
    </v-row>

    <v-alert
      v-if="(!store.isPrenatal || personType === 'FATHER') && sampleType !== ''"
      text
      color="info"
      class="pl-8"
      outlined
    >
      {{
        sampleType === "SALIVA"
          ? "The collection kit and notifications for this family member\
      will be sent to the details provided for the patient above unless an alternative is\
       provided below."
          : "If this individual's address is different to the patient, please enter here"
      }}
      <v-checkbox
        v-if="!store.isPrenatal || personType === 'FATHER'"
        v-model="useDifferentAddress"
        :id="idPrefix + 'useDifferentAddress'"
        :label="
          sampleType === 'SALIVA'
            ? 'Post this collection kit to a different address.'
            : 'Use different address.'
        "
        class="form-no-border mt-4"
      ></v-checkbox>
    </v-alert>
    <v-row v-if="useDifferentAddress">
      <v-col class="custom-col">
        <span class="text-title">Email</span>
        <v-text-field
          v-model="emailAddress"
          :id="idPrefix + 'emailAddress'"
          :rules="emailRule"
          outlined
          :maxlength="limitEmail"
          hide-details="auto"
        ></v-text-field>
      </v-col>
      <v-col class="custom-col">
        <span class="text-title">Mobile*</span>
        <v-text-field
          v-model="mobileNum"
          :id="idPrefix + 'mobileNum'"
          :rules="ruleValidMobile"
          outlined
          :maxlength="limitMobile"
          hide-details="auto"
        ></v-text-field>
      </v-col>
    </v-row>
    <AddressSection
      v-if="useDifferentAddress"
      :ref="idPrefix"
      :idPrefix="idPrefix"
      class="address-class mb-4"
      :id="idPrefix + '_address'"
      filled
    >
    </AddressSection>
  </v-form>
</template>

<script>
import FormRulesMixin from "../mixins/FormRulesMixin";
import DropDownMixin from "../mixins/DropDownMixin";
import { EventBus } from "@/event-bus";
import AddressSection from "./AddressSection";
import { useSectionStore } from "../store/SectionStore";

export default {
  name: "",
  mixins: [FormRulesMixin, DropDownMixin],
  props: {
    title: String,
    idPrefix: String,
    patientdata: Object,
    populatekeys: Array,
    familytype: String,
    testType: String,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
  },
  components: { AddressSection },

  data: () => ({
    personType: "",
    affected: "",
    externalId: "",
    sampleType: "",
    storedSampleId: "",
    nonVCGSSampleID: "",
    lastName: "",
    firstName: "",
    dobyy: "",
    dobmm: "",
    dobdd: "",
    ethnicity: "",
    mobileNum: "",
    emailAddress: "",
    unitFlat: "",
    streetAddress: "",
    city: "",
    state: "",
    postcode: "",
    country: "AUS",
    useDifferentAddress: false,
    addedFamilyRoles: {},
    familyRoleRule: [],
    familyaddress: false,
    prenatalMothersDetails: {},
  }),
  setup() {
    const store = useSectionStore();
    return {
      store,
    };
  },

  mounted() {
    // Load the data from the patientdata given via a prop
    // Only load the keys given
    // if (this.patientdata && this.populatekeys && this.populatekeys.length > 0) {
    //   for (let k of this.populatekeys) {
    //     if (k in this.patientdata) {
    //       this[k] = this.patientdata[k];
    //     }
    //   }
    // }
    EventBus.$on("addFamilyRole", (title, personType) => {
      this.addedFamilyRoles[title] = personType;
      this.familyRoleRule = this.checkFamilyRoles(
        this.addedFamilyRoles,
        this.familytype
      );
    });
  },
  beforeDestroy() {
    EventBus.$emit("removeFamSampleType", {
      testType: this.testType,
      title: this.title,
    });
  },
  watch: {
    personType(newVal, oldVal) {
      // When the personType changes lets emit an event up to alert that its been changed
      EventBus.$emit("addFamilyRole", this.title, this.personType);

      //Load patient data if its prenatal and populate mothers details and removes data if father is chosen
      if (useSectionStore().isPrenatal) {
        if (this.personType === "MOTHER") {
          this.prenatalMothersDetails = useSectionStore().patientDetails;
          this.addMotherData();
        } else if (oldVal === "MOTHER" && newVal === "FATHER") {
          this.clearData();
        }
      }
    },
  },

  computed: {
    dobddRule() {
      if (this.dobdd !== "") {
        if (
          [2, 4, 6, 9, 11].includes(parseInt(this.dobmm)) &&
          parseInt(this.dobdd) === 31
        ) {
          return ["Cannot use 31 with this month"];
        }
        return this.ruleDOBday;
      } else {
        return this.ruleNotNull;
      }
    },
    dobmmRule() {
      return this.dobmm !== "" ? this.ruleDOBmonth : this.ruleNotNull;
    },
    dobyyRule() {
      return this.dobyy !== "" ? this.ruleDOByear : this.ruleNotNull;
    },
    emailRule() {
      if (this.emailAddress === "") {
        return [];
      } else {
        return this.ruleValidEmail;
      }
    },
    processedSampleTypeList() {
      return this.sampleTypeList.map((item) => ({
        ...item,
        disabled: !item.validTests.includes(this.testType),
      }));
    },
  },

  methods: {
    handleSampleTypeChange() {
      if (this.sampleType === "STORED_DATA") {
        const famSampleTypeObj = {
          testType: this.testType,
          title: this.title,
          sampleType: this.sampleType,
        };

        return EventBus.$emit("addFamSampleType", famSampleTypeObj);
      }

      return EventBus.$emit("removeFamSampleType", {
        testType: this.testType,
        title: this.title,
      });
    },
    addMotherData() {
      if (this.prenatalMothersDetails) {
        this["firstName"] = this.prenatalMothersDetails.firstName || "";
        this["lastName"] = this.prenatalMothersDetails.lastName || "";
        this["mobileNum"] = this.prenatalMothersDetails.mobileNum || "";
        this["ethnicity"] = this.prenatalMothersDetails.ethnicity || "";
        this["dobdd"] = this.prenatalMothersDetails.dobdd || "";
        this["dobmm"] = this.prenatalMothersDetails.dobmm || "";
        this["dobyy"] = this.prenatalMothersDetails.dobyy || "";
      }
    },
    clearData() {
      this["firstName"] = "";
      this["lastName"] = "";
      this["mobileNum"] = "";
      this["ethnicity"] = "";
      this["dobdd"] = "";
      this["dobmm"] = "";
      this["dobyy"] = "";
    },
    checkFamilyRoles(addedFamilyRoles, familyType) {
      if (addedFamilyRoles) {
        let loweredRoleValues = Object.values(addedFamilyRoles).map((x) =>
          x.toLowerCase()
        );
        if (familyType.toLowerCase() === "trio") {
          let valid =
            loweredRoleValues.length !== 2 ||
            (loweredRoleValues.includes("mother") &&
              loweredRoleValues.includes("father"));

          return valid
            ? []
            : [
                `Please be sure to select both a Mother and Father when selecting a Trio ${
                  this.testType === "mito_diagnostic" ||
                  this.testType === "whole_genome_sequencing"
                    ? "genome"
                    : "exome"
                }.`,
              ];
        } else {
          // Future family types can be added here for validation.
          return [];
        }
      } else {
        return [];
      }
    },
    birthDate() {
      let val;
      if (!this.dobdd || !this.dobmm || !this.dobyy) {
        val = "";
      } else {
        val =
          this.dobyy +
          "-" +
          this.zfill(2, this.dobmm) +
          "-" +
          this.zfill(2, this.dobdd);
      }
      return val;
    },
    sex() {
      let val;
      if (this.personType === "MOTHER") {
        val = "F"; // F represents female here for downstream use.
      } else if (this.personType === "FATHER") {
        val = "M"; // M represents male here for downstream use.
      } else {
        val = "U"; // U represents unknown here for downstream use.
      }
      return val;
    },
    isFormValid: function() {
      var isValid = true;
      if (this.sampleType === "NON_VCGS_SAMPLE") {
        isValid = isValid;
      }
      if (
        this.useDifferentAddress &&
        !this.$refs[this.idPrefix].validateForm()
      ) {
        return false;
      }
      return isValid;
    },
    transferData: function() {
      let addressData;
      if (this.useDifferentAddress) {
        addressData = this.$refs[this.idPrefix].transferData();
      }

      return {
        personType: this.personType,
        affected: this.affected,
        externalId: this.externalId,
        sampleType: this.sampleType,
        storedSampleId: this.storedSampleId,
        nonVCGSSampleID: this.nonVCGSSampleID,
        lastName: this.lastName,
        firstName: this.firstName,
        birthDate: this.birthDate(),
        ethnicity: this.ethnicity,
        sex: this.sex(),
        mobileNum: this.cleanPhoneNumber(this.mobileNum),
        emailAddress: this.emailAddress,
        unitFlat: this.useDifferentAddress ? addressData.unitFlat : "",
        streetAddress: this.useDifferentAddress
          ? addressData.streetAddress
          : "",
        city: this.useDifferentAddress ? addressData.city : "",
        state: this.useDifferentAddress ? addressData.state : "",
        postcode: this.useDifferentAddress ? addressData.postcode : "",
        country: this.useDifferentAddress ? addressData.country : "AUS",
        useDifferentAddress: this.useDifferentAddress,
      };
    },
  },
};
</script>

<style scoped>
/* Override vuetify2 built-in styling. Read more https://vue-loader.vuejs.org/guide/scoped-css.html#deep-selectors */
.fam-role-class >>> .v-text-field__details {
  overflow: visible !important;
}
</style>
